import { uniq } from "es-toolkit";
import { getStorefrontClient } from "~/lib/shopify/storefront/client";
import type { MetaobjectBadge, ProductForBadge, StockStatus } from "~/lib/badges/types";
import type {
  BadgeDefinitionsQuery,
  ProductsForBadgesQuery,
} from "~/types/shopify/storefront.generated";
import { parseBadgeFields, normalizeTag } from "./transformers";

interface VariantNode {
  price: { amount: string };
  compareAtPrice?: { amount: string } | null;
  availableForSale: boolean;
}

interface ProductNode {
  id: string;
  handle: string;
  tags: string[];
  availableForSale: boolean;
  selectedOrFirstAvailableVariant?: {
    currentlyNotInStock: boolean;
  } | null;
  variants: {
    nodes: VariantNode[];
  };
  collections: {
    edges: Array<{ node: { id: string; handle: string } }>;
  };
}

function deriveStockStatus(node: ProductNode): StockStatus {
  if (!node.availableForSale) return "Out of stock";
  const currentlyNotInStock = node.selectedOrFirstAvailableVariant?.currentlyNotInStock ?? false;
  return currentlyNotInStock ? "Pre-order" : "In stock";
}

// Derives price/compareAtPrice/hasPriceRange from the cheapest variant's own price and
// compare-at price, not an aggregate range — Shopify's priceRange/compareAtPriceRange
// fields are computed independently per field and can't be trusted to pair correctly
// (e.g. compareAtPriceRange includes variants with no discount as 0).
function derivePricing(variants: VariantNode[]): {
  price: number;
  compareAtPrice: number | undefined;
  hasPriceRange: boolean;
} {
  const available = variants.filter((v) => v.availableForSale);
  const candidates = available.length ? available : variants;
  if (!candidates.length) return { price: 0, compareAtPrice: undefined, hasPriceRange: false };

  const prices = candidates.map((v) => Number(v.price.amount));
  const cheapest = candidates[prices.indexOf(Math.min(...prices))];
  const compareAtAmount = cheapest.compareAtPrice ? Number(cheapest.compareAtPrice.amount) : 0;

  return {
    price: Number(cheapest.price.amount),
    compareAtPrice: compareAtAmount > 0 ? compareAtAmount : undefined,
    hasPriceRange: Math.min(...prices) !== Math.max(...prices),
  };
}

const BADGE_DEFINITIONS_QUERY = `#graphql
  query BadgeDefinitions {
    metaobjects(first: 50, type: "product_card_badge", sortKey: "id") {
      edges {
        node {
          id
          fields { key value }
        }
      }
    }
  }
`;

const PRODUCTS_FOR_BADGES_QUERY = `#graphql
  query ProductsForBadges($query: String!) {
    products(first: 250, query: $query) {
      edges {
        node {
          id
          handle
          tags
          availableForSale
          selectedOrFirstAvailableVariant {
            currentlyNotInStock
          }
          # Only the first 10 variants are fetched per product (query-cost tradeoff); this
          # catalog's variants are just size options (colour is a separate product), so real
          # products stay well under this cap. Must be a literal — codegen can't statically
          # parse a JS-interpolated value here.
          variants(first: 10) {
            nodes {
              price { amount }
              compareAtPrice { amount }
              availableForSale
            }
          }
          collections(first: 40) {
            edges { node { id handle } }
          }
        }
      }
    }
  }
`;

export async function fetchBadgeDefinitions(): Promise<MetaobjectBadge[]> {
  const preloaded = window.DUSK?.badgeDefinitions;
  // Deliberate: Task 10's Liquid setup snippet unconditionally emits badgeDefinitions
  // (as [] when the store has no badge metaobjects), and we treat that array as
  // authoritative even when empty — this avoids a wasted Storefront API round-trip on
  // the common case of a store with zero badges configured. Do not switch this to
  // `if (preloaded?.length)`; that would re-enable the fallback query for stores that
  // have genuinely opted out of badges.
  if (preloaded) {
    return preloaded.map(({ id, fields }) => parseBadgeFields(fields, id));
  }

  const client = getStorefrontClient();
  if (!client) return [];

  const response = await client.request<BadgeDefinitionsQuery>(BADGE_DEFINITIONS_QUERY);

  if (response.errors) {
    console.error("[Aurora] fetchBadgeDefinitions error:", response.errors);
    return [];
  }

  const edges = response.data?.metaobjects.edges ?? [];
  return edges.map(({ node }) => parseBadgeFields(node.fields, node.id));
}

// Callers are expected to pass ≤200 deduplicated IDs per call.
// BadgeStore#processBatch chunks its pending list to 200 before invoking this.
export async function fetchProductsForBadges(ids: string[]): Promise<ProductForBadge[]> {
  const unique = uniq(ids);
  if (!unique.length) return [];

  const client = getStorefrontClient();
  if (!client) return [];

  const queryStr = unique.map((id) => `id:${id}`).join(" OR ");
  const response = await client.request<ProductsForBadgesQuery>(PRODUCTS_FOR_BADGES_QUERY, {
    variables: { query: queryStr },
  });

  if (response.errors) {
    console.error("[Aurora] fetchProductsForBadges error:", response.errors);
    return [];
  }

  const edges = response.data?.products.edges ?? [];

  return edges.map(({ node }) => {
    const { price, compareAtPrice, hasPriceRange } = derivePricing(node.variants.nodes);
    return {
      id: node.id.replace("gid://shopify/Product/", ""),
      handle: node.handle,
      tags: node.tags.map(normalizeTag),
      stockStatus: deriveStockStatus(node),
      price,
      compareAtPrice,
      hasPriceRange,
      collections: node.collections.edges.map(({ node: col }) => ({
        id: col.id,
        handle: col.handle,
      })),
    };
  });
}
